import { useT } from '@transifex/react'
import { formatCurrencyValue } from '@vend/utilities'
import { useSelector } from 'react-redux'
import { hasSurchargeRefundable, isReturn } from '../../../functions/sale'
import { EcomCustomCharges } from '../../../models/sale'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useVdCurrencyFilter,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCurrentSale,
  selectEcomCustomCharges,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useTranslatedSurchargePaymentLabel } from '../sale-payment/useSurchargePaymentLabel'
import { CustomPopoverRowProp } from './CustomPopover'

type UseDisplayEcomCustomSurchargesRowReturnType = {
  popoverSurchargesRows: CustomPopoverRowProp[]
  surcharges: EcomCustomCharges
}

/**
 * Hook to get the Ecom custom charges in a sale.
 * This hook is used in the sale popover at sale footer
 */
export const useDisplayEcomCustomSurchargesRow = (): UseDisplayEcomCustomSurchargesRowReturnType => {
  const LS_PAY_SURCHARGE_SOURCE_ID = 'lspay-surcharge-id'
  const ecomCustomCharges = useAppSelector(selectEcomCustomCharges)
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()
  const t = useT()

  const sale = useSelector(selectCurrentSale)
  const isReturnSale = isReturn(sale)
  const vdCurrencyFilter = useVdCurrencyFilter()
  const popoverSurchargesRows: CustomPopoverRowProp[] = []
  const saleManager = useSaleManager()
  const surchargePaymentLabel = useTranslatedSurchargePaymentLabel()

  const hasExtraSurcharge = sale.finals && hasSurchargeRefundable(sale)

  // Deep copy ecomCustomCharges to surcharges (including nested objects like charges)
  const surcharges: EcomCustomCharges = {
    total: ecomCustomCharges?.total ?? 0,
    totalIncl: ecomCustomCharges?.totalIncl ?? 0,
    totalTax: ecomCustomCharges?.totalTax ?? 0,
    charges: ecomCustomCharges?.charges ? [...ecomCustomCharges.charges] : [],
  }

  // Add LS Pay surcharge if it exists
  if (hasExtraSurcharge) {
    const lsPaySurchargeAmount = Number(
      sale.finals.surchargeDisplayProperties.surcharge
    )
    surcharges.charges.push({
      name: surchargePaymentLabel,
      sourceId: LS_PAY_SURCHARGE_SOURCE_ID,
      total: lsPaySurchargeAmount,
      totalIncl: lsPaySurchargeAmount,
      totalTax: 0,
    })
    surcharges.total += lsPaySurchargeAmount
    surcharges.totalIncl += lsPaySurchargeAmount
  }

  surcharges?.charges.forEach((charge, _) => {
    const isNotLsPaySurcharge = charge.sourceId !== LS_PAY_SURCHARGE_SOURCE_ID
    popoverSurchargesRows.push({
      id: charge.sourceId,
      name: charge.name,
      value: isNotLsPaySurcharge
        ? vdCurrencyFilter(charge.totalIncl)
        : formatCurrencyValue(
            charge.totalIncl,
            retailer.culture,
            currentOutlet.currency,
            currentOutlet.currency_symbol
          ),
      button: {
        isDisable: false,
        isVisible: isReturnSale && isNotLsPaySurcharge,
        label: t('Delete ecom custom charge', { _context: 'Button label' }),
        onClick: isReturnSale
          ? saleManager.removeEcomCustomCharge
          : () => false,
      },
    })
  })

  return { popoverSurchargesRows, surcharges }
}
